import { Injectable } from '@angular/core';
import { forkJoin, Observable } from 'rxjs';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { tap, map } from 'rxjs/operators';
import { XmlparseService } from '../xml/xmlparse.service';
import { DomainService } from 'projects/icemedx-lib/src/services/domain/domain.service';
import { LoginService } from 'projects/icemedx-lib/src/login/login.service';

import { AlertService } from "../../services/alert/alert.service";
import { IceTypes } from "../../models/icetypes";
import { Iceutility } from "../../services/iceutility";
import { managementguideline } from 'projects/icemedx-lib/src/models/ehr/subs/managementguideline';
import { diagnosis } from 'projects/icemedx-lib/src/models/ehr/diagnosis';
import { patient } from '../../models/domain/patient';
import { provider_reference } from '../../models/domain/provider_reference';
import { subscriber } from '../../models/domain/subscriber';
import { benefitinformation } from '../../models/ehr/subs/benefitinformation';
import { coverage } from '../../models/ehr/subs/coverage';
import { encounterindex } from '../../models/ehr/encounterindex';
import { encounterprogressnotes } from '../../models/ehr/encounterprogressnotes';

declare var require: any;

@Injectable({
  providedIn: 'root'
})
export class PatientService extends DomainService {

  constructor(http: HttpClient, alertSvc: AlertService, loginSvc: LoginService) {
    super(http, alertSvc, loginSvc);
  }

  downloadDocument(domain: string, key: string, srcType: string) {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Patient service is not configured.";

    let blobUrl = null,
      url = envs[currentEnv].icemedx_api + "/patients/default.aspx?domain=" + domain + "&data=file&providerdomain=&key=" + key + "&subkeys=&type=" + srcType,
      userInfo = super.getLoginService().GetSessionUserInfo();

    if (super.getLoginService().IsSignedIn(userInfo)) {
      var auth = super.getLoginService().GetAccessToken(userInfo);
      var req = new XMLHttpRequest();

      req.open("GET", url, true);
      req.responseType = "blob";
      req.setRequestHeader("Authorization", "Bearer " + auth);

      let ptSvc: PatientService = this;
      req.onload = function (event) {
        var blob = req.response;
        var newBlob = new Blob([blob], { type: blob.type });
        blobUrl = window.URL.createObjectURL(newBlob);
        window.open(blobUrl);
        setTimeout(function () {
          window.URL.revokeObjectURL(blobUrl);
        }, 100);
      };
      req.onreadystatechange = function () {
        if (req.readyState === 4) {
          if (req.status != 200) {
            ptSvc.logMessage(IceTypes.LOGTYPE.ltError, req.statusText);
          }
        }
      }
      req.send();
    }
  }

  getAllAppointments(ptact: string, pact: string, pactdomaintype: IceTypes.ACCOUNT_TYPE, searchprovider: string, from: string, to: string, keywords: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Patient service is not configured.";

    let url = envs[currentEnv].icemedx_api + "/patients?domain=" + ptact + "&data=allpatientappointments" + "&providerdomain=" + pact + "&key=" + "&subkeys=" + "domaintype|" + pactdomaintype.toString() + ";searchprovider|" + searchprovider + ";from|" + from + ";to|" + to + ";keywords|" + keywords;
    return this.getHttp().get(url, { responseType: 'text' }).pipe(
      map(
        response => {
          let data = this.geticexdocumentobjects(response, ["APPOINTMENT_INDEX"]);
          if (Iceutility.isdefined(data)) {
            return data.sort((appt1, appt2) => appt2.time_slot_start - appt1.time_slot_start);
          }
          return [];
        }
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  getMedications(ptact: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Patient service is not configured.";

    let url = envs[currentEnv].icemedx_api + "/patients?domain=" + ptact + "&data=" + "medicationlist";
    return this.getHttp().get(url, { responseType: 'text' }).pipe(
      map(
        response => this.geticexdocumentobjects(response, ["MEDICATION"])
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  getProblems(ptact: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Patient service is not configured.";

    let url = envs[currentEnv].icemedx_api + "/patients?domain=" + ptact + "&data=" + "diagnosislist";
    return this.getHttp().get(url, { responseType: 'text' }).pipe(
      map(
        response => this.geticexdocumentobjects(response, ["DIAGNOSIS"])
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  getCareTeam(ptact: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Patient service is not configured.";

    let url = envs[currentEnv].icemedx_api + "/patients?domain=" + ptact + "&data=providers&providerdomain=&key=";
    return this.getHttp().get(url, { responseType: 'text' }).pipe(
      map(
        response => this.geticexdocumentobjects(response, ["PROVIDER_REFERENCE", "PROVIDER_INDEX"])
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  getAllergies(ptact: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Patient service is not configured.";

    let url = envs[currentEnv].icemedx_api + "/patients?domain=" + ptact + "&data=" + "allergylist";
    return this.getHttp().get(url, { responseType: 'text' }).pipe(
      map(
        response => this.geticexdocumentobjects(response, ["ALLERGIES"])
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  getPatientDocuments(ptact: string, pact: string, pacttype: IceTypes.ACCOUNT_TYPE, searchprovider: string, fromdate: string, todate: string, keywords: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Patient service is not configured.";

    let url = envs[currentEnv].icemedx_api + "/patients?domain=" + ptact + "&data=documents" + "&providerdomain=" + pact + "&key=&subkeys=domaintype:" + pacttype.toString() + ";searchprovider:" + searchprovider + ";from:" + fromdate + ";to:" + todate + ";keywords:";
    return this.getHttp().get(url, { responseType: 'text' }).pipe(
      map(
        response => this.geticexdocumentobjects(response, ["EXTERNAL_DATA"])
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }
  
  getPatientSubscribers(ptact: string, pact: string, pacttype: IceTypes.ACCOUNT_TYPE): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Patient service is not configured.";

    let url = envs[currentEnv].icemedx_api + "/patients?domain=" + ptact + "&data=subscribers" + "&providerdomain=" + pact + "&key=&subkeys=domaintype:" + pacttype.toString() + ";";
    return this.getHttp().get(url, { responseType: 'text' }).pipe(
      map(
        response => this.geticexdocumentobjects(response, ["SUBSCRIBER_REFERENCE", "SUBSCRIBER"])
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  getPayerCoverageBenefits(sub: subscriber, checkactive: boolean): any[] {
    let pbm_src = null;
    if (sub == null)
      return null;

    try {
      let covgs = sub["COVGS"];
      if (covgs != null) {
        let removeDuplicatePayers = (cvgs, key) => {
          if (cvgs == null) return [];
          return Array.from(new Map(
            cvgs.map(cov => [key(cov), cov])
            ).values()
          );
        }

        let newcovgs = removeDuplicatePayers(covgs, c => c.coverage_key);
        let termdate = sub.termination_date == null || sub.termination_date.length == 0 ? null : Iceutility.ConvertFromDotNetServerTime(sub.termination_date);
        let effdate = sub.effective_date == null || sub.effective_date.length == 0 ? null : Iceutility.ConvertFromDotNetServerTime(sub.effective_date);
        let curDate = Iceutility.GetLocalDate();

        for (let icov = 0; icov < newcovgs.length; icov++) {
          let cov: coverage = <coverage>newcovgs[icov];
          if (cov != null) {
            let payername = cov["payer_name"];
            let coverage_key = cov["coverage_key"] == null ? "" : cov["coverage_key"];
            let payer_key = cov["payer_key"] == null ? "" : cov["payer_key"];
            let planname = "";
            let summary = [];
            let ids = cov["IDS"];
            if (ids != null && ids.length > 0) {
              for (let i = 0; i < ids.length; i++) {
                var id = ids[i];
                if (id != null) {
                  let qualcode = id["identification_code_qualifier"];
                  let refidnum = id["identification_code"];
                  if (qualcode == "MI") {
                    payername += " " + refidnum;
                    summary.push("Member ID: " + refidnum);
                    break;
                  }
                }
              }
            }

            summary.push("Status date: " + ""); // to do
            let effDtDesc = "Unknown";
            if (effdate != null)
              effDtDesc = Iceutility.getDateTextEx(effdate, null, false);

            let termDtDesc = "Unknown";
            if (termdate != null)
              termDtDesc = Iceutility.getDateTextEx(termdate, null, false);

            summary.push("Coverage period: " + effDtDesc + " - " + termDtDesc);

            let conNums = "";
            if (cov.CONS.length > 0 && cov.CONS[0].communication_number.length > 0)
              conNums = cov.CONS[0].communication_number_qualifier + ": " + cov.CONS[0].communication_number;

            if (cov.CONS.length > 1 && cov.CONS[1].communication_number.length > 0)
              conNums += cov.CONS[1].communication_number_qualifier + ": " + cov.CONS[1].communication_number;

            if (conNums.length > 0)
              summary.push("Contact: " + conNums);

            let inactive = false;
            if (checkactive) {
              var bfts = cov["BFTS"];
              if (bfts != null) {

                for (var iben = 0; iben < bfts.length; iben++) {
                  let ben: benefitinformation = bfts[iben];
                  if (ben != null) {
                    var bencode = ben["benefit_code"];
                    if (bencode == IceTypes.BenefitCodes.bcInactive) {
                      inactive = true;

                      if (pbm_src == null) {
                        pbm_src = [];
                      }
                      pbm_src.push({ payer_name: payername + " (No suggested coverage - Active)", payer_id: sub.subscriber_key + "|" + coverage_key + "|" + payer_key, subscriber_key: sub.subscriber_key, summary: summary });
                      break;
                    }

                    if (ben["plan_coverage_description"] != null && ben["plan_coverage_description"].length > 0) {
                      planname += " " + ben["plan_coverage_description"];
                    }

                    switch (bencode) {
                      case (IceTypes.BenefitCodes.bcCopayment): {
                        summary.push(PatientService.getBenefitCodeDescription(bencode) + ": " + Iceutility.toDouble(ben.benefit_amount).toString());
                        break;
                      }
                      case (IceTypes.BenefitCodes.bcCoinsurance): {
                        summary.push(PatientService.getBenefitCodeDescription(bencode) + ": " + Iceutility.toDouble(ben.benefit_percent).toString() + "%");
                        break;
                      }
                      case (IceTypes.BenefitCodes.bcDeductible): {
                        summary.push(PatientService.getBenefitCodeDescription(bencode) + ": " + Iceutility.toDouble(ben.benefit_amount).toString());
                        break;
                      }
                    }
                  }
                }
              }
              
              if (effdate != null) {
                if (curDate >= effdate) {
                  if (termdate == null) {
                    inactive = true;
                  }
                  else if (curDate <= termdate) {
                    inactive = true;
                  }
                }
              }

              if (inactive)
                payername += " - " + planname + " (Active)";
              else
                payername += " - " + planname + " (Inactive)";
            }

            if (pbm_src == null)
              pbm_src = [];

            pbm_src.push({ payer_name: payername, payer_id: sub.subscriber_key + "|" + coverage_key + "|" + payer_key, subscriber_key: sub.subscriber_key, summary: summary });
          }
        }
      }
    } catch (e) {
      console.log(e);
    }
    return pbm_src;
  }

  getOrders(ptact, keywords, start, due, searchtype, nextrecord, totalrecord, ordertype, minimalData: boolean): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Patient service is not configured.";

    let url = envs[currentEnv].icemedx_api + "/patients?domain=" + ptact + "&data=allpatientorders" + "&providerdomain=" + "&key=&subkeys=domaintype:;searchprovider:;start:" + start + ";due:" + due + ";keywords:;searchtype:" + searchtype + ";next_start:" + nextrecord + ";total_records:" + totalrecord + ";minimal_data:" + minimalData.toString() +  "&key=";
    return this.getHttp().get(url, { responseType: 'text' }).pipe(
      map(
        response => this.geticexdocumentobjects(response, ["ORDER_ENTRY_INDEX"])
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  getAlerts(currentpt, currentact, currentacttype, alerttype, keywords, code, srctype, srckey, user, subtypes, glkey: string) {
    if (Iceutility.isdefined(currentpt) == false)
      currentpt = "";

    if (Iceutility.isdefined(currentact) == false)
      currentact = "";

    if (Iceutility.isdefined(alerttype) == false)
      alerttype = "";

    if (Iceutility.isdefined(code) == false)
      code = "";

    if (Iceutility.isdefined(keywords) == false)
      keywords = "";

    if (Iceutility.isdefined(user) == false)
      user = "";

    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Patient service is not configured.";

    let url = envs[currentEnv].icemedx_api + "/patients?domain=" + currentpt + "&data=alerts&providerdomain=" + currentact + "&key=" + glkey + "&subkeys=type:" + alerttype + ";srctype:" + srctype + ";srckey:" + srckey + ";keywords:" + keywords + ";user:" + user + ";code:" + code + ";domaintype:" + currentacttype;
    return this.getHttp().get(url, { responseType: 'text' }).pipe(
      map(
        response => this.geticexdocumentobjects(response, ["MANAGEMENT_GUIDELINE_INDEX"])
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  getCDSResults(currentpt, currentact, currentacttype, alerttype, keywords, code, srctype, srckey, user, subtypes, glkey: string) {
    if (Iceutility.isdefined(currentpt) == false)
      currentpt = "";

    if (Iceutility.isdefined(currentact) == false)
      currentact = "";

    if (Iceutility.isdefined(alerttype) == false)
      alerttype = "";

    if (Iceutility.isdefined(code) == false)
      code = "";

    if (Iceutility.isdefined(keywords) == false)
      keywords = "";

    if (Iceutility.isdefined(user) == false)
      user = "";

    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Patient service is not configured.";

    let url = envs[currentEnv].icemedx_api + "/patients?domain=" + currentpt + "&data=cdsresults&providerdomain=" + currentact + "&key=" + glkey + "&subkeys=type:" + alerttype + ";srctype:" + srctype + ";srckey:" + srckey + ";keywords:" + keywords + ";user:" + user + ";code:" + code + ";domaintype:" + currentacttype;
    return this.getHttp().get(url, { responseType: 'text' }).pipe(
      map(
        response => this.geticexdocumentobjects(response, ["MANAGEMENT_GUIDELINE_INDEX", "REPORT_DATA"])
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  getEncounterList(ptAct: string, providerDomain: string, domaintype: IceTypes.ACCOUNT_TYPE, provider: string, fromdate: string, todate: string, keywords: string) {
    if (Iceutility.isdefined(ptAct) == false) throw "Patient service: invalid patient domain for encounter list.";
    if (Iceutility.isdefined(providerDomain) == false) throw "Patient service: invalid provider domain for encounter list.";

    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Patient service is not configured.";

    let url = envs[currentEnv].icemedx_api + "/patients?domain=" + ptAct + "&data=encounters&providerdomain=" + providerDomain + "&key=&subkeys=domaintype|" + domaintype + ";searchprovider|" + provider + ";from|" + fromdate + ";to|" + todate + ";keywords|" + keywords;
    return this.getHttp().get(url, { responseType: 'text' }).pipe(
      map(
        response => this.geticexdocumentobjects(response, ["ENCOUNTER_INDEX"])
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  getNotesList(ptAct: string, act: string, acttype: IceTypes.ACCOUNT_TYPE, provider: string, fromdate: any, todate: any, keywords: string) {
    if (Iceutility.isdefined(ptAct) == false) throw "Patient service: invalid patient domain for encounter list.";
    if (Iceutility.isdefined(act) == false) throw "Patient service: invalid provider domain for encounter list.";

    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Patient service is not configured.";

    let url = envs[currentEnv].icemedx_api + "/patients?domain=" + ptAct + "&data=notes&providerdomain=" + act.toString() + "&key=&subkeys=domaintype|" + acttype + ";searchprovider|" + provider + ";from|" + fromdate + ";to|" + todate + ";keywords|" + keywords;
    return this.getHttp().get(url, { responseType: 'text' }).pipe(
      map(
        response => this.geticexdocumentobjects(response, ["ENCOUNTER_PROGRESS_NOTES"])
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }


  getEncountersByKey(providerDomain: string, ptAcct: string, keys: string[], domainType: IceTypes.ACCOUNT_TYPE): Observable<any> {
    let promises = [];

    for (let key of keys) {
      promises.push(this.getEncounter(providerDomain, ptAcct, key, domainType));
    }

    return forkJoin(promises);
  }

  getEncounter(providerDomain: string, ptAcct: string, key: string, domainType: IceTypes.ACCOUNT_TYPE): Observable<encounterindex> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Patient service is not configured.";

    let url = envs[currentEnv].icemedx_api + "/providers/default.aspx?domain=" + providerDomain + "&key=" + key + "&data=encounter&acttype=" + domainType + "&patientdomain=" + ptAcct + "&subkeys=";
    return this.getHttp().get(url, { responseType: 'text' }).pipe(
      map(
        response => this.geticexdocumentobjects(response, ["ENCOUNTER"])
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  insertUpdateRule(ptAct: string, prodomain: string, domaintype: IceTypes.ACCOUNT_TYPE, rule: managementguideline) {
    if (Iceutility.isdefined(rule) == false) throw "Patient service insertUpdate rule has invalid parameters.";

    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Patient service is not configured.";

    let url = envs[currentEnv].icemedx_api + "/patients/default.aspx?domain=" + ptAct + "&data=" + (rule.offline_record_status == IceTypes.offlineRecStatus.offlineInsert ? "rule" : "updaterule") + "&providerdomain=" + prodomain + "&key=" + "&subkeys=" + "domaintype|" + domaintype;
    let tblname = new managementguideline().gettablename().toLowerCase();
    let data = "{" + JSON.stringify(tblname) + ": " + JSON.stringify(rule) + "}";
    return this.getHttp().post(url, data, { responseType: 'text' }).pipe(
      map(
        response => this.geticexdocumentobjects(response, ["MANAGEMENT_GUIDELINE"])
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  insertUpdateDiagnsosis(ptAct: string, prodomain: string, domaintype: IceTypes.ACCOUNT_TYPE, diag: diagnosis): Observable<any> {
    if (Iceutility.isdefined(diag) == false) throw "Patient service Update diagnosis has invalid parameters.";

    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Patient service is not configured.";

    let url = envs[currentEnv].icemedx_api + "/patients/default.aspx?domain=" + ptAct + "&data=" + (diag.offline_record_status == IceTypes.offlineRecStatus.offlineInsert ? "diagnosis" : "updatediagnosis");
    let tblname = diag.gettablename().toLowerCase();
    let data = "{" + JSON.stringify(tblname) + ": " + JSON.stringify(diag) + "}";
    return this.getHttp().post(url, data, { responseType: 'text' }).pipe(
      map(
        response => this.geticexdocumentobjects(response, ["DIAGNOSIS"])
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }
 
  insertUpdateNote(ptAct: string, prodomain: string, domaintype: IceTypes.ACCOUNT_TYPE, notedata: encounterprogressnotes): Observable<any> {
    if (Iceutility.isdefined(notedata) == false) throw "Patient service Update encounter note has invalid parameters.";

    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Patient service is not configured.";

    let url = envs[currentEnv].icemedx_api + "/patients/default.aspx?domain=" + ptAct + "&data=" + (notedata.offline_record_status == IceTypes.offlineRecStatus.offlineInsert ? "note" : "updatenote");
    url += "&providerdomain=" + prodomain + "&key=" + "&subkeys=domaintype:" + domaintype.toString();
    let tblname = notedata.gettablename().toLowerCase();
    let data = "{" + JSON.stringify(tblname) + ": " + JSON.stringify(notedata) + "}";
    return this.getHttp().post(url, data, { responseType: 'text' }).pipe(
      map(
        response => this.geticexdocumentobjects(response, ["ENCOUNTER_PROGRESS_NOTES"])
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  insertUpdateProviders(ptAct: string, proRefs: provider_reference[]): Observable<any> {
    if (Iceutility.isdefined(proRefs) == false || proRefs.length == 0) throw "Patient service Update providers has invalid parameters.";

    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Patient service is not configured.";

    let url = envs[currentEnv].icemedx_api + "/patients/default.aspx?domain=" + ptAct + "&data=providers";
    let tblname = proRefs[0].gettablename().toLowerCase();
    let data = "{" + JSON.stringify(tblname) + ": " + JSON.stringify(proRefs) + "}";
    return this.getHttp().post(url, data, { responseType: 'text' }).pipe(
      map(
        response => this.geticexdocumentobjects(response, ["PROVIDER_REFERENCE"])
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  insertPatient(prodomain: string, domaintype: IceTypes.ACCOUNT_TYPE, pt: patient, members: string): Observable<any> {
    if (Iceutility.isdefined(pt) == false) throw "Patient service Insert patient has invalid parameters.";

    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Patient service is not configured.";

    let url = envs[currentEnv].icemedx_api + "/patients/default.aspx?domain=&data=adddemo&providerdomain=" + prodomain + "&key=&subkeys=domaintype:" + domaintype.toString() + ";lastname:;members:" + (members == null ? "" : members);
    let tblname = pt.gettablename().toLowerCase();
    let data = "{" + JSON.stringify(tblname) + ": " + JSON.stringify(pt) + "}";
    return this.getHttp().post(url, data, { responseType: 'text' }).pipe(
      map(
        response => this.geticexdocumentobjects(response, ["PATIENT"])
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  updatePatient(prodomain: string, domaintype: IceTypes.ACCOUNT_TYPE, pt: patient, members: string): Observable<any> {
    if (Iceutility.isdefined(pt) == false) throw "Patient service Insert patient has invalid parameters.";

    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Patient service is not configured.";

    let url = envs[currentEnv].icemedx_api + "/patients/default.aspx?domain=" + pt.trans_account + "&data=demo&providerdomain=" + prodomain + "&key=&subkeys=domaintype:" + domaintype.toString() + ";lastname:;members:" + (members == null ? "" : members);
    let tblname = pt.gettablename().toLowerCase();
    let data = "{" + JSON.stringify(tblname) + ": " + JSON.stringify(pt) + "}";
    return this.getHttp().post(url, data, { responseType: 'text' }).pipe(
      map(
        response => this.geticexdocumentobjects(response, ["PATIENT"])
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  insertSubscriber(prodomain: string, domaintype: IceTypes.ACCOUNT_TYPE, sub: subscriber, members: string): Observable<any> {
    if (Iceutility.isdefined(sub) == false) throw "Patient service Insert patient has invalid parameters.";

    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Patient service is not configured.";

    let url = envs[currentEnv].icemedx_api + "/subscribers/default.aspx?domain=&data=addsubscriber&key=&subkeys=domaintype:" + domaintype.toString() + ";lastname:;members:" + (members == null ? "" : members) + ";domainact:" + prodomain;
    let tblname = sub.gettablename().toLowerCase();
    let data = "{" + JSON.stringify(tblname) + ": " + JSON.stringify(sub) + "}";
    return this.getHttp().post(url, data, { responseType: 'text' }).pipe(
      map(
        response => this.geticexdocumentobjects(response, ["SUBSCRIBER"])
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  updateSubscriber(prodomain: string, domaintype: IceTypes.ACCOUNT_TYPE, sub: subscriber, members: string): Observable<any> {
    if (Iceutility.isdefined(sub) == false) throw "Patient service Insert patient has invalid parameters.";

    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Patient service is not configured.";

    let url = envs[currentEnv].icemedx_api + "/subscribers/default.aspx?domain=" + sub.subscriber_key + "&data=updatesubscriber&providerdomain=" + prodomain + "&key=&subkeys=domaintype:" + domaintype.toString() + ";lastname:;members:" + (members == null ? "" : members) + ";domainact:" + prodomain;
    let tblname = sub.gettablename().toLowerCase();
    let data = "{" + JSON.stringify(tblname) + ": " + JSON.stringify(sub) + "}";
    return this.getHttp().post(url, data, { responseType: 'text' }).pipe(
      map(
        response => this.geticexdocumentobjects(response, ["SUBSCRIBER"])
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  static getProblemState(type: IceTypes.problemstate): string {
    if (type == null)
      return "";

    try {
      switch (Iceutility.toLong(type)) {
        case IceTypes.problemstate.stateCurrent:
          return "Current";
        case IceTypes.problemstate.statePast:
          return "Past";
      }
    } catch (e) {
      console.error(e.message);
    }
    return "";
  }

  static getProblemStates(): any {
    var types = [];
    try {
      types.push({ value: IceTypes.problemstate.stateCurrent.toString(), name: PatientService.getProblemState(IceTypes.problemstate.stateCurrent) });
      types.push({ value: IceTypes.problemstate.statePast.toString(), name: PatientService.getProblemState(IceTypes.problemstate.statePast) });
    } catch (e) {
      console.log(e);
    }
    return types;
  }

  static getProblemStatus(type: IceTypes.problemstatus): string {
    if (type == null)
      return "";

    try {
      switch (Iceutility.toLong(type)) {
        case IceTypes.problemstatus.statusActive:
          return "Active";
        case IceTypes.problemstatus.statusResolved:
          return "Resolved";
      }
    } catch (e) {
      console.error(e.message);
    }
    return "";
  }

  static getProblemStatuses(): any {
    var types = [];
    try {
      types.push({ value: IceTypes.problemstatus.statusActive.toString(), name: PatientService.getProblemStatus(IceTypes.problemstatus.statusActive) });
      types.push({ value: IceTypes.problemstatus.statusResolved.toString(), name: PatientService.getProblemStatus(IceTypes.problemstatus.statusResolved) });
    } catch (e) {
      console.log(e);
    }
    return types;
  }

  static getSeverityType(type: IceTypes.severitytype): string {
    if (type == null)
      return;

    try {
      switch (Iceutility.toLong(type)) {
        case IceTypes.severitytype.servTypeCritical:
        default:
          return "Critical";
        case IceTypes.severitytype.servTypeIntensive:
          return "Intensive";
        case IceTypes.severitytype.servTypeMild:
          return "Mild";
        case IceTypes.severitytype.servTypeNonCritical:
          return "Non-Critical";
        case IceTypes.severitytype.servTypeUnknown:
          return "Unknown";
      }
    } catch (e) {
      console.error(e.message);
    }
  }

  static getObservationType(type: IceTypes.observationtype): string {
    if (type == null)
      return;

    try {
      switch (Iceutility.toLong(type)) {
        case IceTypes.observationtype.observTypeEpisode:
        default:
          return "Episode";
        case IceTypes.observationtype.observTypeMedicationStatus:
          return "Medication Status";
        case IceTypes.observationtype.observTypeNote:
          return "Note";
        case IceTypes.observationtype.observTypePatientAnnotations:
          return "Patient Annotations";
        case IceTypes.observationtype.observTypePatientAwareness:
          return "Patient Awareness";
        case IceTypes.observationtype.observTypePrecondition:
          return "Precondition";
        case IceTypes.observationtype.observTypeReaction:
          return "Reaction";
        case IceTypes.observationtype.observTypeResult:
          return "Result";
        case IceTypes.observationtype.observTypeStatusChange:
          return "Status Change";
        case IceTypes.observationtype.observTypeSymptom:
          return "Symptom";
        case IceTypes.observationtype.observTypeVitals:
          return "Vitals";
        case IceTypes.observationtype.observTypeAllergies:
          return "Allergies";
      }
    } catch (e) {
      console.error(e.message);
    }
  }

  static getObservations() {
    var observations = [];
    try {
      observations.push({ value: IceTypes.observationtype.observTypeEpisode, name: "Episode" });
      observations.push({ value: IceTypes.observationtype.observTypeMedicationStatus, name: "Medication Status" });
      observations.push({ value: IceTypes.observationtype.observTypeNote, name: "Note" });
      observations.push({ value: IceTypes.observationtype.observTypePatientAnnotations, name: "Patient Annotations" });
      observations.push({ value: IceTypes.observationtype.observTypePatientAwareness, name: "Patient Awareness" });
      observations.push({ value: IceTypes.observationtype.observTypePrecondition, name: "Precondition" });
      observations.push({ value: IceTypes.observationtype.observTypeReaction, name: "Reaction" });
      observations.push({ value: IceTypes.observationtype.observTypeResult, name: "Result" });
      observations.push({ value: IceTypes.observationtype.observTypeStatusChange, name: "Status Change" });
      observations.push({ value: IceTypes.observationtype.observTypeSymptom, name: "Symptom" });
      observations.push({ value: IceTypes.observationtype.observTypeVitals, name: "Vitals" });
      observations.push({ value: IceTypes.observationtype.observTypeAllergies, name: "Allergies" });
      observations.sort(function (a, b) { return a.name.localeCompare(b.name) });
    } catch (e) {
      console.error(e);
    }
    return observations;
  }

  static getSeverities() {
    var severity = [];
    try {
      severity.push({ value: IceTypes.severitytype.servTypeCritical, name: "Critical" });
      severity.push({ value: IceTypes.severitytype.servTypeIntensive, name: "Intensive" });
      severity.push({ value: IceTypes.severitytype.servTypeMild, name: "Mild" });
      severity.push({ value: IceTypes.severitytype.servTypeNonCritical, name: "Non-Critical" });
      severity.push({ value: IceTypes.severitytype.servTypeUnknown, name: "Unknown" });
      severity.sort(function (a, b) { return a.name.localeCompare(b.name) });
    } catch (e) {
      console.error(e);
    }
    return severity;
  }

  static getBenefitCodeDescription(bc: IceTypes.BenefitCodes): string {
    switch (bc) {
      case IceTypes.BenefitCodes.bcActiveCoverage: { return "Active"; }
      case IceTypes.BenefitCodes.bcInactive: { return "Inactive"; }
      case IceTypes.BenefitCodes.bcCannotProcess: { return "Cannot Process"; }
      case IceTypes.BenefitCodes.bcOutofPocket: { return "Out of Pocket"; }
      case IceTypes.BenefitCodes.bcNonCovered: { return "Non-Covered"; }
      case IceTypes.BenefitCodes.bcCoinsurance: { return "Co-insurance"; }
      case IceTypes.BenefitCodes.bcCopayment: { return "Co-payment"; }
      case IceTypes.BenefitCodes.bcDeductible: { return "Deductible limits and balance"; }
      case IceTypes.BenefitCodes.bcContactEntity: { return "Contact Entity"; }
      case IceTypes.BenefitCodes.bcPCPInfo: { return "PCP related Information"; }
      case IceTypes.BenefitCodes.bcBenefitLimits: { return "Benefit limits"; }
    }
  }

  static getBenefitCodes(): any[] {
    let ret = [];
    try {
      ret.push({ value: IceTypes.BenefitCodes.bcActiveCoverage, name: this.getBenefitCodeDescription(IceTypes.BenefitCodes.bcActiveCoverage) });
      ret.push({ value: IceTypes.BenefitCodes.bcInactive, name: this.getBenefitCodeDescription(IceTypes.BenefitCodes.bcInactive) });
      ret.push({ value: IceTypes.BenefitCodes.bcCannotProcess, name: this.getBenefitCodeDescription(IceTypes.BenefitCodes.bcCannotProcess) });
      ret.push({ value: IceTypes.BenefitCodes.bcOutofPocket, name: this.getBenefitCodeDescription(IceTypes.BenefitCodes.bcOutofPocket) });
      ret.push({ value: IceTypes.BenefitCodes.bcNonCovered, name: this.getBenefitCodeDescription(IceTypes.BenefitCodes.bcNonCovered) });
      ret.push({ value: IceTypes.BenefitCodes.bcCoinsurance, name: this.getBenefitCodeDescription(IceTypes.BenefitCodes.bcCoinsurance) });
      ret.push({ value: IceTypes.BenefitCodes.bcCopayment, name: this.getBenefitCodeDescription(IceTypes.BenefitCodes.bcCopayment) });
      ret.push({ value: IceTypes.BenefitCodes.bcDeductible, name: this.getBenefitCodeDescription(IceTypes.BenefitCodes.bcDeductible) });
      ret.push({ value: IceTypes.BenefitCodes.bcPCPInfo, name: this.getBenefitCodeDescription(IceTypes.BenefitCodes.bcPCPInfo) });
      ret.push({ value: IceTypes.BenefitCodes.bcBenefitLimits, name: this.getBenefitCodeDescription(IceTypes.BenefitCodes.bcBenefitLimits) });
    } catch (e) {
      console.log(e);
    }
    return ret;
  }
}
